'use client'

import { useCallback, useEffect, useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useSession } from '@marketplace-web/shared/session-data'
import { getCurrentUser } from '@marketplace-web/registration/identity-data'

import { getBusinessAccountAddresses } from '../api/business-address'
import { transformBusinessAccountAddressesResponse } from '../transformers/business-address'
import { businessAccountFromUserDto } from '../utils/business-account'

const useFetchBusinessAddresses = () => {
  const { user } = useSession()
  const businessAccountId = businessAccountFromUserDto(user)?.id

  const [businessAccountIdState, setBusinessAccountIdState] = useState<number>()
  const [isUserFetchLoading, setIsUserFetchLoading] = useState(false)
  const [userError, setUserError] = useState<Error>()

  const fetchCurrentUser = useCallback(async () => {
    const response = await getCurrentUser()

    if ('errors' in response) {
      setUserError(new Error(response.errors[0]!.value))
      setIsUserFetchLoading(false)

      return
    }

    setBusinessAccountIdState(businessAccountFromUserDto(response?.user)?.id)
  }, [setUserError, setBusinessAccountIdState])

  const {
    fetch: fetchBusinessAddresses,
    data: businessAddressesInitialResponse,
    transformedData: businessAddressesResponse,
    isLoading: isAddressFetchLoading,
    error: addressError,
  } = useFetch(getBusinessAccountAddresses, transformBusinessAccountAddressesResponse)

  useEffect(() => {
    if (!businessAccountId) {
      setIsUserFetchLoading(true)
      fetchCurrentUser()
      if (businessAccountIdState) {
        setIsUserFetchLoading(false)
        fetchBusinessAddresses({ businessAccountId: businessAccountIdState })
      }

      return
    }
    fetchBusinessAddresses({ businessAccountId })
  }, [fetchBusinessAddresses, businessAccountId, businessAccountIdState, fetchCurrentUser])

  return {
    businessAddressesResponse,
    businessAddressesInitialResponse,
    businessAccountId: businessAccountId || businessAccountIdState,
    isLoading: isAddressFetchLoading || isUserFetchLoading,
    error: addressError || userError,
  }
}

export default useFetchBusinessAddresses
